import React, { useState, useEffect, useRef, useCallback } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import SnapshotKPICard from './components/SnapshotKPICard';
import MonthlyHighlights from './components/MonthlyHighlights';
import YearlyHighlights from './components/YearlyHighlights';
import MonthlyTrendChart from './components/MonthlyTrendChart';
import AIInsightPanel from './components/AIInsightPanel';
import ValidationBanner from './components/ValidationBanner';
import {
  fetchMonthlySnapshot,
  fetchYearlySnapshot,
  fetchDepartments,
} from '../../services/executiveSnapshotService';
import { validateMonthlyKPIs, validateYearlyKPIs, validatePDFData,  } from './utils/snapshotValidation';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const currentYear = new Date()?.getFullYear();
const currentMonth = new Date()?.getMonth() + 1;
const YEARS = Array.from({ length: 6 }, (_, i) => currentYear - i);

const fmtOMR = (v) =>
  v == null || v === undefined
    ? '—'
    : `OMR ${Number(v)?.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const fmtPct = (v) =>
  v == null || v === undefined ? '—' : `${Number(v)?.toFixed(1)}%`;

const calcTrend = (current, prev) => {
  if (!prev || prev === 0) return { trend: undefined, trendValue: undefined };
  const pct = ((current - prev) / prev) * 100;
  return {
    trend: pct > 0 ? 'up' : pct < 0 ? 'down' : 'neutral',
    trendValue: `${Math.abs(pct)?.toFixed(0)}%`,
  };
};

// ─── Section Header ───────────────────────────────────────────────────────────
const SectionHeader = ({ icon, iconColor, title, subtitle, badge = undefined }) => (
  <div className="flex items-center gap-3 mb-5">
    <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${iconColor}`}>
      <Icon name={icon} size={16} />
    </div>
    <div className="flex-1">
      <div className="flex items-center gap-2.5">
        <h2 className="text-base font-bold text-foreground">{title}</h2>
        {badge && (
          <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full font-medium">
            {badge}
          </span>
        )}
      </div>
      {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
    </div>
  </div>
);

// ─── Stat Summary Bar ─────────────────────────────────────────────────────────
const StatSummaryBar = ({ items }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 bg-muted/30 rounded-2xl border border-border/50">
    {items?.map((item, i) => (
      <div key={i} className="text-center">
        <div className={`text-lg font-bold ${item?.color || 'text-foreground'}`}>{item?.value}</div>
        <div className="text-[11px] text-muted-foreground font-medium mt-0.5">{item?.label}</div>
      </div>
    ))}
  </div>
);

// ─── PDF Export ───────────────────────────────────────────────────────────────
const buildPDF = async ({ monthlyData, yearlyData, selectedMonth, selectedYear, selectedDept, pdfValidation }) => {
  const { default: jsPDF } = await import('jspdf');

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = 210;
  const pageH = 297;
  const margin = 14;
  const contentW = pageW - margin * 2;
  const TOTAL_PAGES = 4;

  // ── Premium Brand Palette ──
  const NAVY        = [8, 20, 55];
  const NAVY_MID    = [18, 40, 95];
  const NAVY_LIGHT  = [32, 62, 135];
  const GOLD        = [194, 152, 35];
  const GOLD_BRIGHT = [220, 175, 50];
  const GOLD_LIGHT  = [255, 251, 228];
  const GOLD_MID    = [235, 198, 80];
  const ACCENT      = [20, 98, 200];
  const ACCENT_LIGHT= [232, 243, 255];
  const GREEN       = [18, 135, 72];
  const GREEN_LIGHT = [225, 248, 235];
  const RED         = [195, 38, 38];
  const RED_LIGHT   = [255, 236, 236];
  const AMBER       = [195, 115, 0];
  const AMBER_LIGHT = [255, 246, 225];
  const TEAL        = [0, 132, 132];
  const TEAL_LIGHT  = [218, 248, 248];
  const PURPLE      = [115, 40, 170];
  const PURPLE_LIGHT= [246, 235, 255];
  const SLATE       = [55, 75, 105];
  const GRAY_DARK   = [28, 38, 55];
  const GRAY_MID    = [88, 105, 128];
  const GRAY_LIGHT  = [245, 247, 252];
  const GRAY_BORDER = [208, 216, 230];
  const WHITE       = [255, 255, 255];
  const OFF_WHITE   = [249, 250, 254];
  const CREAM       = [252, 250, 244];

  const setColor = (r, g, b) => pdf?.setTextColor(r, g, b);
  const setFill  = (r, g, b) => pdf?.setFillColor(r, g, b);
  const setDraw  = (r, g, b) => pdf?.setDrawColor(r, g, b);

  const monthName  = MONTHS?.[selectedMonth - 1] || '';
  const reportDate = new Date()?.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
  const companyName = 'Electronic City for IT Services';
  const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  // ── Load logo ──
  const loadLogoBase64 = () => new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = '/assets/images/ecity_logo.png';
  });

  const logoBase64 = await loadLogoBase64();

  // ══════════════════════════════════════════════════════════════════
  // SHARED DRAWING HELPERS
  // ══════════════════════════════════════════════════════════════════

  // ── Page Header (pages 2-4) ──
  const drawPageHeader = (pageNum, pageTitle, pageSubtitle) => {
    // Full navy header band
    setFill(...NAVY);
    pdf?.rect(0, 0, pageW, 20, 'F');
    // Gold accent line
    setFill(...GOLD_BRIGHT);
    pdf?.rect(0, 20, pageW, 1.2, 'F');
    // Subtle side accent
    setFill(...NAVY_LIGHT);
    pdf?.rect(0, 0, 4, 20, 'F');

    // Logo
    if (logoBase64) {
      try { pdf?.addImage(logoBase64, 'PNG', margin, 3.5, 22, 13); } catch {}
    } else {
      pdf?.setFont('helvetica', 'bold');
      pdf?.setFontSize(10);
      setColor(...GOLD_BRIGHT);
      pdf?.text('eCITY', margin + 2, 13);
    }

    // Page title center
    pdf?.setFont('helvetica', 'bold');
    pdf?.setFontSize(10);
    setColor(...WHITE);
    pdf?.text(pageTitle?.toUpperCase(), pageW / 2, 9, { align: 'center' });
    if (pageSubtitle) {
      pdf?.setFont('helvetica', 'normal');
      pdf?.setFontSize(6.5);
      setColor(175, 198, 238);
      pdf?.text(pageSubtitle, pageW / 2, 15.5, { align: 'center' });
    }

    // Page indicator right
    pdf?.setFont('helvetica', 'normal');
    pdf?.setFontSize(6.5);
    setColor(155, 180, 225);
    pdf?.text(`${monthName} ${selectedYear}  ·  Page ${pageNum} / ${TOTAL_PAGES}`, pageW - margin, 13, { align: 'right' });

    return 26;
  };

  // ── Page Footer ──
  const drawPageFooter = (pageNum) => {
    const fy = pageH - 12;
    setFill(...NAVY);
    pdf?.rect(0, fy, pageW, 12, 'F');
    setFill(...GOLD_BRIGHT);
    pdf?.rect(0, fy, pageW, 0.8, 'F');

    pdf?.setFont('helvetica', 'normal');
    pdf?.setFontSize(6);
    setColor(145, 170, 215);
    pdf?.text(companyName, margin, fy + 7);
    pdf?.text(`${monthName} ${selectedYear}  ·  Confidential — For Management Use Only`, pageW / 2, fy + 7, { align: 'center' });
    pdf?.text(`${pageNum} / ${TOTAL_PAGES}`, pageW - margin, fy + 7, { align: 'right' });
  };

  // ── Section Title with accent bar ──
  const drawSectionTitle = (title, y, accentRGB = NAVY_MID, subtitle = '') => {
    // Accent bar
    setFill(...accentRGB);
    pdf?.roundedRect(margin, y, 4, 10, 1.5, 1.5, 'F');
    pdf?.setFont('helvetica', 'bold');
    pdf?.setFontSize(10);
    setColor(...NAVY);
    pdf?.text(title, margin + 8, y + 7);
    if (subtitle) {
      pdf?.setFont('helvetica', 'normal');
      pdf?.setFontSize(7);
      setColor(...GRAY_MID);
      pdf?.text(subtitle, margin + 8 + pdf?.getTextWidth(title) + 4, y + 7);
    }
    // Thin rule
    setDraw(...GRAY_BORDER);
    pdf?.setLineWidth(0.25);
    const titleEnd = margin + 8 + pdf?.getTextWidth(title) + (subtitle ? pdf?.getTextWidth(subtitle) + 8 : 4);
    pdf?.line(titleEnd, y + 5, pageW - margin, y + 5);
    pdf?.setLineWidth(0.2);
    return y + 16;
  };

  // ── Premium KPI Card (Page 1 style) ──
  const drawPremiumKPICard = (x, y, w, h, label, currentVal, prevVal, growthPct, accentRGB, iconEmoji) => {
    // Drop shadow
    setFill(215, 222, 238);
    pdf?.roundedRect(x + 0.8, y + 0.8, w, h, 3.5, 3.5, 'F');
    // Card background
    setFill(...WHITE);
    setDraw(...GRAY_BORDER);
    pdf?.setLineWidth(0.25);
    pdf?.roundedRect(x, y, w, h, 3.5, 3.5, 'FD');
    pdf?.setLineWidth(0.2);
    // Top color bar
    setFill(...accentRGB);
    pdf?.roundedRect(x, y, w, 3, 2, 2, 'F');
    pdf?.rect(x, y + 1.5, w, 1.5, 'F');

    // Icon circle (top right)
    const iconBgR = Math.min(255, accentRGB[0] + 185);
    const iconBgG = Math.min(255, accentRGB[1] + 185);
    const iconBgB = Math.min(255, accentRGB[2] + 185);
    setFill(iconBgR, iconBgG, iconBgB);
    pdf?.circle(x + w - 9, y + 11, 5.5, 'F');
    pdf?.setFont('helvetica', 'bold');
    pdf?.setFontSize(8);
    setColor(...accentRGB);
    pdf?.text(iconEmoji || '●', x + w - 9, y + 13.5, { align: 'center' });

    // Label
    pdf?.setFont('helvetica', 'normal');
    pdf?.setFontSize(6.5);
    setColor(...GRAY_MID);
    pdf?.text(label?.toUpperCase(), x + 4.5, y + 10);

    // Current value
    const valFontSize = (currentVal?.length || 0) > 14 ? 8 : (currentVal?.length || 0) > 10 ? 9.5 : 11;
    pdf?.setFont('helvetica', 'bold');
    pdf?.setFontSize(valFontSize);
    setColor(...GRAY_DARK);
    pdf?.text(currentVal || '—', x + 4.5, y + h - 10);

    // Previous & growth row
    if (prevVal || growthPct != null) {
      const rowY = y + h - 4.5;
      pdf?.setFont('helvetica', 'normal');
      pdf?.setFontSize(5.8);
      setColor(...GRAY_MID);
      if (prevVal) pdf?.text(`Prev: ${prevVal}`, x + 4.5, rowY);
      if (growthPct != null) {
        const isPos = growthPct >= 0;
        const growthColor = isPos ? GREEN : RED;
        setFill(...(isPos ? GREEN_LIGHT : RED_LIGHT));
        const growthText = `${isPos ? '▲' : '▼'} ${Math.abs(growthPct)?.toFixed(1)}%`;
        const gx = prevVal ? x + 4.5 + pdf?.getTextWidth(`Prev: ${prevVal}`) + 3 : x + 4.5;
        const gw = pdf?.getTextWidth(growthText) + 4;
        pdf?.roundedRect(gx - 1, rowY - 3.5, gw, 5, 1.5, 1.5, 'F');
        pdf?.setFont('helvetica', 'bold');
        pdf?.setFontSize(5.8);
        setColor(...growthColor);
        pdf?.text(growthText, gx + 1, rowY);
      }
    }
  };

  // ── Business Highlight Card ──
  const drawHighlightCard = (x, y, w, h, badgeLabel, title, subtitle, accentRGB, bgRGB, rankIcon = '') => {
    // Shadow
    setFill(215, 222, 238);
    pdf?.roundedRect(x + 0.7, y + 0.7, w, h, 3, 3, 'F');
    // Card
    setFill(...bgRGB);
    setDraw(...accentRGB);
    pdf?.setLineWidth(0.35);
    pdf?.roundedRect(x, y, w, h, 3, 3, 'FD');
    pdf?.setLineWidth(0.2);
    // Left accent stripe
    setFill(...accentRGB);
    pdf?.roundedRect(x, y, 4, h, 1.5, 1.5, 'F');
    pdf?.rect(x + 2, y, 2, h, 'F');

    // Badge pill
    const bw = pdf?.getTextWidth(badgeLabel) + 8;
    setFill(...accentRGB);
    pdf?.roundedRect(x + 7, y + 4.5, bw, 6.5, 2, 2, 'F');
    pdf?.setFont('helvetica', 'bold');
    pdf?.setFontSize(5.8);
    setColor(...WHITE);
    pdf?.text(badgeLabel, x + 11, y + 9);

    // Rank icon (top right)
    if (rankIcon) {
      pdf?.setFont('helvetica', 'normal');
      pdf?.setFontSize(12);
      setColor(...accentRGB);
      pdf?.text(rankIcon, x + w - 9, y + 14);
    }

    // Title
    pdf?.setFont('helvetica', 'bold');
    pdf?.setFontSize(8.5);
    setColor(...GRAY_DARK);
    const titleLines = pdf?.splitTextToSize(title || 'No Data', w - 18);
    pdf?.text(titleLines?.[0], x + 7, y + 20);

    // Subtitle
    pdf?.setFont('helvetica', 'normal');
    pdf?.setFontSize(7);
    setColor(...GRAY_MID);
    const subLines = pdf?.splitTextToSize(subtitle || '—', w - 18);
    pdf?.text(subLines?.slice(0, 2), x + 7, y + 27.5);
  };

  // ── Health Score Gauge ──
  const drawHealthGauge = (x, y, w, score, status, statusColor) => {
    const h = 42;
    // Background card
    setFill(...NAVY);
    pdf?.roundedRect(x, y, w, h, 4, 4, 'F');
    // Gold top bar
    setFill(...GOLD_BRIGHT);
    pdf?.roundedRect(x, y, w, 3, 2, 2, 'F');
    pdf?.rect(x, y + 1.5, w, 1.5, 'F');

    // Title
    pdf?.setFont('helvetica', 'bold');
    pdf?.setFontSize(7);
    setColor(175, 198, 238);
    pdf?.text('BUSINESS HEALTH SCORE', x + w / 2, y + 10, { align: 'center' });

    // Score arc background
    const cx = x + w / 2;
    const cy = y + 28;
    const r = 10;
    setFill(20, 45, 110);
    pdf?.circle(cx, cy, r + 2, 'F');

    // Score arc fill (simple colored circle)
    const scoreColor = score >= 85 ? GREEN : score >= 70 ? ACCENT : score >= 55 ? AMBER : RED;
    setFill(...scoreColor);
    pdf?.circle(cx, cy, r, 'F');
    setFill(...NAVY);
    pdf?.circle(cx, cy, r * 0.62, 'F');

    // Score number
    pdf?.setFont('helvetica', 'bold');
    pdf?.setFontSize(11);
    setColor(...GOLD_BRIGHT);
    pdf?.text(String(score), cx, cy + 4, { align: 'center' });

    // /100
    pdf?.setFont('helvetica', 'normal');
    pdf?.setFontSize(5.5);
    setColor(155, 180, 225);
    pdf?.text('/ 100', cx + 7, cy + 4);

    // Status badge
    setFill(...statusColor);
    pdf?.roundedRect(x + w / 2 - 16, y + h - 10, 32, 7.5, 2, 2, 'F');
    pdf?.setFont('helvetica', 'bold');
    pdf?.setFontSize(7);
    setColor(...WHITE);
    pdf?.text(status, x + w / 2, y + h - 4.5, { align: 'center' });
  };

  // ── Progress bar ──
  const drawProgressBar = (x, y, w, pct, color, label, valueLabel) => {
    const h = 5.5;
    pdf?.setFont('helvetica', 'normal');
    pdf?.setFontSize(6.5);
    setColor(...GRAY_MID);
    pdf?.text(label, x, y - 1.5);
    pdf?.setFont('helvetica', 'bold');
    setColor(...color);
    pdf?.text(valueLabel, x + w, y - 1.5, { align: 'right' });
    // Track
    setFill(218, 224, 238);
    pdf?.roundedRect(x, y, w, h, 2.5, 2.5, 'F');
    // Fill
    const fillW = Math.max(0, Math.min(w, (pct / 100) * w));
    if (fillW > 0) {
      setFill(...color);
      pdf?.roundedRect(x, y, fillW, h, 2.5, 2.5, 'F');
    }
  };

  // ── Bar chart ──
  const drawBarChart = (x, y, w, h, data, labelKey, valueKey, barColor, secondaryKey, secondaryColor) => {
    if (!data?.length) return y + h + 6;
    const maxVal = Math.max(...data?.map(d => Math.max(d?.[valueKey] || 0, d?.[secondaryKey] || 0)), 1);
    const barAreaH = h - 22;
    const barW = (w - 6) / data?.length;

    // Chart background
    setFill(...OFF_WHITE);
    setDraw(...GRAY_BORDER);
    pdf?.setLineWidth(0.25);
    pdf?.roundedRect(x, y, w, h, 2.5, 2.5, 'FD');
    pdf?.setLineWidth(0.2);

    // Gridlines
    setDraw(225, 230, 242);
    pdf?.setLineDashPattern([1.5, 2.5], 0);
    for (let g = 1; g <= 4; g++) {
      const gy = y + 8 + barAreaH - (barAreaH * g / 4);
      pdf?.line(x + 4, gy, x + w - 4, gy);
      const gVal = (maxVal * g / 4);
      const gLabel = gVal >= 1000 ? `${(gVal / 1000)?.toFixed(0)}K` : String(Math.round(gVal));
      pdf?.setFont('helvetica', 'normal');
      pdf?.setFontSize(4.5);
      setColor(...GRAY_MID);
      pdf?.text(gLabel, x + 3, gy - 0.5);
    }
    pdf?.setLineDashPattern([], 0);

    data?.forEach((d, i) => {
      const val = d?.[valueKey] || 0;
      const bh = Math.max(0, (val / maxVal) * barAreaH);
      const bx = x + 4 + i * barW + barW * 0.12;
      const bw = secondaryKey ? barW * 0.38 : barW * 0.72;
      const by = y + 8 + barAreaH - bh;

      setFill(...barColor);
      if (bh > 0) pdf?.roundedRect(bx, by, bw, bh, 1, 1, 'F');

      if (secondaryKey) {
        const val2 = d?.[secondaryKey] || 0;
        const bh2 = Math.max(0, (val2 / maxVal) * barAreaH);
        const bx2 = bx + bw + 1.5;
        const by2 = y + 8 + barAreaH - bh2;
        setFill(...secondaryColor);
        if (bh2 > 0) pdf?.roundedRect(bx2, by2, bw, bh2, 1, 1, 'F');
      }

      // Month label
      pdf?.setFont('helvetica', 'normal');
      pdf?.setFontSize(5.5);
      setColor(...GRAY_MID);
      const lbl = String(d?.[labelKey] || '')?.substring(0, 3);
      pdf?.text(lbl, x + 4 + i * barW + barW / 2, y + h - 6, { align: 'center' });

      // Highlight selected month
      if (d?.month === selectedMonth) {
        setDraw(...GOLD_BRIGHT);
        pdf?.setLineWidth(0.5);
        pdf?.line(x + 4 + i * barW + barW / 2, y + h - 5, x + 4 + i * barW + barW / 2, y + h - 3);
        pdf?.setLineWidth(0.2);
      }
    });

    return y + h + 6;
  };

  // ── Donut chart ──
  const drawDonutChart = (cx, cy, r, segments) => {
    let startAngle = -Math.PI / 2;
    segments?.forEach(seg => {
      if (seg?.pct <= 0) return;
      const endAngle = startAngle + (seg?.pct / 100) * 2 * Math.PI;
      const steps = Math.max(3, Math.round(seg?.pct * 0.7));
      pdf?.setFillColor(...seg?.color);
      pdf?.setDrawColor(...seg?.color);
      for (let s = 0; s < steps; s++) {
        const a1 = startAngle + (s / steps) * (endAngle - startAngle);
        const a2 = startAngle + ((s + 1) / steps) * (endAngle - startAngle);
        const x1 = cx + r * Math.cos(a1);
        const y1 = cy + r * Math.sin(a1);
        const x2 = cx + r * Math.cos(a2);
        const y2 = cy + r * Math.sin(a2);
        pdf?.triangle(cx, cy, x1, y1, x2, y2, 'F');
      }
      startAngle = endAngle;
    });
    // Inner white circle for donut
    setFill(...WHITE);
    pdf?.circle(cx, cy, r * 0.58, 'F');
  };

  // ── Compute Business Health Score ──
  const computeHealthScore = () => {
    let score = 50;
    const rev = monthlyData?.totalRevenue || 0;
    const prevRev = monthlyData?.prevRevenue || 0;
    const margin = monthlyData?.profitMargin || 0;
    const delayed = monthlyData?.delayedProjects || 0;
    const closedDeals = monthlyData?.closedDeals || 0;
    const openQ = monthlyData?.openQuotations || 0;
    const outstanding = monthlyData?.outstandingPayments || 0;

    if (rev > 0) score += 10;
    if (prevRev > 0 && rev >= prevRev) score += 10;
    if (margin >= 20) score += 15;
    else if (margin >= 10) score += 8;
    else if (margin > 0) score += 3;
    if (closedDeals > 0) score += 8;
    if (openQ > 0) score += 5;
    if (delayed === 0) score += 7;
    else if (delayed <= 2) score -= 5;
    else score -= 12;
    if (outstanding > rev * 0.5) score -= 5;

    score = Math.max(10, Math.min(100, score));
    const status = score >= 85 ? 'Excellent' : score >= 70 ? 'Good' : score >= 55 ? 'Moderate' : 'Needs Attention';
    const statusColor = score >= 85 ? GREEN : score >= 70 ? ACCENT : score >= 55 ? AMBER : RED;
    return { score, status, statusColor };
  };

  // ── Generate executive summary ──
  const generateExecutiveSummary = () => {
    const rev = monthlyData?.totalRevenue || 0;
    const profit = monthlyData?.totalProfit || 0;
    const prevRev = monthlyData?.prevRevenue || 0;
    const closedDeals = monthlyData?.closedDeals || 0;
    const openQ = monthlyData?.openQuotations || 0;
    const activeP = monthlyData?.activeProjects || 0;
    const margin = monthlyData?.profitMargin || 0;
    const growthPct = prevRev > 0 ? ((rev - prevRev) / prevRev * 100) : null;
    const growthText = growthPct != null
      ? ` Business performance ${growthPct >= 0 ? 'improved' : 'declined'} by ${Math.abs(growthPct)?.toFixed(1)}% compared to the previous month.`
      : '';
    return `During ${monthName} ${selectedYear}, the company achieved ${fmtOMR(rev)} in total revenue and ${fmtOMR(profit)} in net profit, reflecting a ${fmtPct(margin)} profit margin.${growthText} A total of ${closedDeals} deal${closedDeals !== 1 ? 's were' : ' was'} successfully closed while ${openQ} quotation${openQ !== 1 ? 's remain' : ' remains'} active in the pipeline. ${activeP} project${activeP !== 1 ? 's are' : ' is'} currently in execution. Management attention should focus on collections and accelerating quotation-to-project conversion to support continued growth.`;
  };

  // ── Generate CEO Corner ──
  const generateCEOCorner = () => {
    const rev = monthlyData?.totalRevenue || 0;
    const prevRev = monthlyData?.prevRevenue || 0;
    const profit = monthlyData?.totalProfit || 0;
    const margin = monthlyData?.profitMargin || 0;
    const openQ = monthlyData?.openQuotations || 0;
    const delayed = monthlyData?.delayedProjects || 0;
    const growthPct = prevRev > 0 ? ((rev - prevRev) / prevRev * 100) : null;
    const growthPhrase = growthPct != null
      ? `with ${growthPct >= 0 ? 'a positive revenue trend of ' + Math.abs(growthPct)?.toFixed(1) + '%' : 'a revenue adjustment of ' + Math.abs(growthPct)?.toFixed(1) + '%'} compared to the prior month`
      : 'with stable revenue performance';
    return `The company maintained solid performance during ${monthName} ${selectedYear} ${growthPhrase} and a ${fmtPct(margin)} profit margin. ${profit > 0 ? `Net profit of ${fmtOMR(profit)} reflects continued operational discipline.` : ''} ${openQ > 0 ? `Management attention should focus on converting the ${openQ} active quotation${openQ !== 1 ? 's' : ''} in the pipeline.` : ''} ${delayed > 0 ? `${delayed} delayed project${delayed !== 1 ? 's require' : ' requires'} immediate escalation.` : 'All projects remain on schedule.'} The leadership team is encouraged to maintain momentum and accelerate deal closures heading into the next reporting period.`;
  };

  // ── Generate recommendations ──
  const generateRecommendations = () => {
    const recs = [];
    const rev = monthlyData?.totalRevenue || 0;
    const prevRev = monthlyData?.prevRevenue || 0;
    const openQ = monthlyData?.openQuotations || 0;
    const pipeline = monthlyData?.pipelineValue || 0;
    const delayed = monthlyData?.delayedProjects || 0;
    const margin = monthlyData?.profitMargin || 0;
    const topDept = monthlyData?.topDepartment;
    const outstanding = monthlyData?.outstandingPayments || 0;

    if (openQ > 0 && pipeline > 0) recs?.push(`Focus on converting the ${openQ} active quotation${openQ !== 1 ? 's' : ''} (${fmtOMR(pipeline)} pipeline) into closed deals to accelerate revenue growth.`);
    if (outstanding > 0) recs?.push(`Improve collection follow-up for outstanding receivables of ${fmtOMR(outstanding)}. Prioritize overdue invoices to strengthen cash flow.`);
    if (delayed > 0) recs?.push(`Monitor ${delayed} delayed project${delayed !== 1 ? 's' : ''} closely. Assign dedicated resources and escalate to management to prevent further slippage.`);
    if (topDept) recs?.push(`Increase investment and focus on ${topDept}, the highest-performing department, to sustain and grow its revenue contribution.`);
    if (margin > 0 && margin < 15) recs?.push(`Review pricing strategy and cost structure to improve profit margin from the current ${fmtPct(margin)} toward the target of 20%+.`);
    if (prevRev > 0 && rev < prevRev) recs?.push(`Address the revenue decline compared to last month. Review sales pipeline, accelerate deal closures, and identify new business opportunities.`);
    if (recs?.length < 3) recs?.push('Review low-performing sales opportunities and consider reassigning resources to higher-potential accounts.');
    if (recs?.length < 4) recs?.push('Conduct a monthly performance review meeting with department heads to align on targets and address operational challenges.');
    if (recs?.length < 5) recs?.push('Expand the highest-profit service line and replicate successful project models to drive margin improvement.');
    return recs?.slice(0, 5);
  };

  const calcGrowthPct = (current, prev) => {
    if (!prev || prev === 0) return null;
    return ((current - prev) / prev) * 100;
  };

  // ══════════════════════════════════════════════════════════════════
  // PAGE 1 — EXECUTIVE BUSINESS SNAPSHOT
  // ══════════════════════════════════════════════════════════════════

  // ── Full navy background ──
  setFill(...NAVY);
  pdf?.rect(0, 0, pageW, pageH, 'F');

  // ── Decorative geometric elements ──
  setFill(15, 35, 88);
  pdf?.circle(pageW + 5, -5, 55, 'F');
  setFill(12, 28, 72);
  pdf?.circle(pageW - 15, 25, 35, 'F');
  setFill(18, 42, 100);
  pdf?.circle(-10, pageH + 10, 50, 'F');

  // ── Gold accent bars ──
  setFill(...GOLD_BRIGHT);
  pdf?.rect(0, 0, pageW, 3.5, 'F');
  setFill(...GOLD);
  pdf?.rect(0, pageH - 3.5, pageW, 3.5, 'F');

  // ── Left accent stripe ──
  setFill(...NAVY_LIGHT);
  pdf?.rect(0, 3.5, 5, pageH - 7, 'F');

  // ── White content card ──
  setFill(...WHITE);
  pdf?.roundedRect(14, 16, pageW - 28, pageH - 32, 6, 6, 'F');

  // ── Gold top accent on card ──
  setFill(...GOLD_BRIGHT);
  pdf?.roundedRect(14, 16, pageW - 28, 4.5, 3, 3, 'F');
  pdf?.rect(14, 18.5, pageW - 28, 2, 'F');

  // ── Logo area ──
  let logoY = 28;
  if (logoBase64) {
    try { pdf?.addImage(logoBase64, 'PNG', pageW / 2 - 30, logoY, 60, 22); } catch {}
    logoY += 26;
  } else {
    pdf?.setFont('helvetica', 'bold');
    pdf?.setFontSize(22);
    setColor(...NAVY);
    pdf?.text('eCITY', pageW / 2, logoY + 14, { align: 'center' });
    logoY += 20;
  }

  // ── Company name ──
  pdf?.setFont('helvetica', 'bold');
  pdf?.setFontSize(13);
  setColor(...NAVY);
  pdf?.text(companyName, pageW / 2, logoY + 5, { align: 'center' });

  // ── Gold divider ──
  setFill(...GOLD_BRIGHT);
  pdf?.rect(pageW / 2 - 38, logoY + 9, 76, 0.9, 'F');

  // ── Report title ──
  pdf?.setFont('helvetica', 'bold');
  pdf?.setFontSize(17);
  setColor(...NAVY_MID);
  pdf?.text('Monthly Executive Brief', pageW / 2, logoY + 19, { align: 'center' });

  // ── Reporting period badge ──
  setFill(...NAVY);
  pdf?.roundedRect(pageW / 2 - 34, logoY + 23, 68, 13, 3.5, 3.5, 'F');
  setFill(...GOLD_BRIGHT);
  pdf?.roundedRect(pageW / 2 - 34, logoY + 23, 68, 2, 2, 2, 'F');
  pdf?.rect(pageW / 2 - 34, logoY + 24, 68, 1, 'F');
  pdf?.setFont('helvetica', 'bold');
  pdf?.setFontSize(11);
  setColor(...GOLD_BRIGHT);
  pdf?.text(`${monthName} ${selectedYear}`, pageW / 2, logoY + 32, { align: 'center' });

  // ── Generated date ──
  pdf?.setFont('helvetica', 'normal');
  pdf?.setFontSize(7);
  setColor(...GRAY_MID);
  pdf?.text(`Report Generated: ${reportDate}`, pageW / 2, logoY + 42, { align: 'center' });

  // ── Thin divider ──
  setDraw(...GRAY_BORDER);
  pdf?.setLineWidth(0.3);
  pdf?.line(24, logoY + 47, pageW - 24, logoY + 47);
  pdf?.setLineWidth(0.2);

  // ── KPI CARDS (2 rows × 3 cols) ──
  const kpiStartY = logoY + 52;
  const kpiW = (contentW - 10) / 3;
  const kpiH = 34;
  const kpiGap = 5;

  const revGrowth = calcGrowthPct(monthlyData?.totalRevenue || 0, monthlyData?.prevRevenue || 0);

  const kpiCards = [
    {
      label: 'Total Revenue',
      val: fmtOMR(monthlyData?.totalRevenue || 0),
      prev: monthlyData?.prevRevenue > 0 ? fmtOMR(monthlyData?.prevRevenue) : null,
      growth: revGrowth,
      color: ACCENT,
      icon: '$',
    },
    {
      label: 'Net Profit',
      val: fmtOMR(monthlyData?.totalProfit || 0),
      prev: null,
      growth: null,
      color: GREEN,
      icon: '↑',
    },
    {
      label: 'Profit Margin',
      val: fmtPct(monthlyData?.profitMargin || 0),
      prev: null,
      growth: null,
      color: TEAL,
      icon: '%',
    },
    {
      label: 'Closed Deals',
      val: String(monthlyData?.closedDeals ?? 0),
      prev: null,
      growth: null,
      color: [0, 138, 98],
      icon: '✓',
    },
    {
      label: 'Active Projects',
      val: String(monthlyData?.activeProjects ?? 0),
      prev: null,
      growth: null,
      color: NAVY_LIGHT,
      icon: '▶',
    },
    {
      label: 'Outstanding Collections',
      val: fmtOMR(monthlyData?.outstandingPayments || monthlyData?.pipelineValue || 0),
      prev: null,
      growth: null,
      color: PURPLE,
      icon: '◈',
    },
  ];

  kpiCards?.forEach((kpi, i) => {
    const col = i % 3;
    const row = Math.floor(i / 3);
    drawPremiumKPICard(
      margin + col * (kpiW + kpiGap),
      kpiStartY + row * (kpiH + kpiGap),
      kpiW, kpiH,
      kpi?.label, kpi?.val, kpi?.prev, kpi?.growth, kpi?.color, kpi?.icon
    );
  });

  const afterKPIs = kpiStartY + 2 * (kpiH + kpiGap) + 5;

  // ── Executive Summary ──
  // Section header bar
  setFill(...NAVY);
  pdf?.roundedRect(margin, afterKPIs, contentW, 9, 2.5, 2.5, 'F');
  setFill(...GOLD_BRIGHT);
  pdf?.roundedRect(margin, afterKPIs, contentW, 2, 1.5, 1.5, 'F');
  pdf?.rect(margin, afterKPIs + 1, contentW, 1, 'F');
  pdf?.setFont('helvetica', 'bold');
  pdf?.setFontSize(8.5);
  setColor(...GOLD_BRIGHT);
  pdf?.text('EXECUTIVE SUMMARY', pageW / 2, afterKPIs + 6.2, { align: 'center' });

  const summaryText = generateExecutiveSummary();
  const summaryLines = pdf?.splitTextToSize(summaryText, contentW - 14);
  const summaryH = Math.max(22, summaryLines?.length * 4.8 + 14);

  setFill(247, 249, 255);
  setDraw(...ACCENT);
  pdf?.setLineWidth(0.3);
  pdf?.roundedRect(margin, afterKPIs + 10, contentW, summaryH, 2.5, 2.5, 'FD');
  pdf?.setLineWidth(0.2);
  setFill(...ACCENT);
  pdf?.roundedRect(margin, afterKPIs + 10, 4, summaryH, 1.5, 1.5, 'F');
  pdf?.rect(margin + 2, afterKPIs + 10, 2, summaryH, 'F');

  // Quote mark decoration
  pdf?.setFont('helvetica', 'bold');
  pdf?.setFontSize(30);
  setColor(220, 230, 248);
  pdf?.text('"', margin + 6, afterKPIs + 22);

  pdf?.setFont('helvetica', 'normal');
  pdf?.setFontSize(7.8);
  setColor(...GRAY_DARK);
  pdf?.text(summaryLines, margin + 8, afterKPIs + 18);

  // ── Page 1 footer ──
  pdf?.setFont('helvetica', 'normal');
  pdf?.setFontSize(6.5);
  setColor(95, 115, 155);
  pdf?.text(`${companyName}  ·  Confidential — For Management Use Only  ·  Page 1 of ${TOTAL_PAGES}`, pageW / 2, pageH - 6, { align: 'center' });

  // ══════════════════════════════════════════════════════════════════
  // PAGE 2 — BUSINESS HIGHLIGHTS
  // ══════════════════════════════════════════════════════════════════
  pdf?.addPage();
  let y = drawPageHeader(2, 'Business Highlights', `${monthName} ${selectedYear} — Key Achievements & Business Health`);

  // ── Highlight Cards (3 cols × 2 rows) ──
  y = drawSectionTitle('Monthly Business Highlights', y, GOLD, 'Top performers and key achievements');

  const hlW = (contentW - 10) / 3;
  const hlH = 40;
  const hlGap = 5;

  const highlights = [
    {
      badge: 'LARGEST DEAL',
      title: monthlyData?.largestDeal?.name || 'No Closed Deals',
      sub: monthlyData?.largestDeal
        ? `${fmtOMR(monthlyData?.largestDeal?.value)}  ·  ${monthlyData?.largestDeal?.client || 'N/A'}`
        : 'No closed deals this period',
      accent: GOLD,
      bg: GOLD_LIGHT,
      icon: '🏆',
    },
    {
      badge: 'TOP CUSTOMER',
      title: monthlyData?.topCustomer || 'No Customer Data',
      sub: monthlyData?.topCustomer
        ? `Revenue: ${fmtOMR(monthlyData?.topCustomerRevenue)}`
        : 'No customer data available',
      accent: ACCENT,
      bg: ACCENT_LIGHT,
      icon: '🏆',
    },
    {
      badge: 'BEST DEPARTMENT',
      title: monthlyData?.topDepartment || 'No Department Data',
      sub: monthlyData?.topDepartment
        ? `Contribution: ${fmtOMR(monthlyData?.topDepartmentRevenue)}`
        : 'No department data',
      accent: GREEN,
      bg: GREEN_LIGHT,
      icon: '🏆',
    },
    {
      badge: 'HIGHEST PROFIT PROJECT',
      title: yearlyData?.highestProfitProject?.name || monthlyData?.largestDeal?.name || 'No Data',
      sub: yearlyData?.highestProfitProject
        ? `Profit: ${fmtOMR(yearlyData?.highestProfitProject?.profit)}`
        : 'No project data',
      accent: TEAL,
      bg: TEAL_LIGHT,
      icon: '🏆',
    },
    {
      badge: 'TOP SALES PERFORMER',
      title: monthlyData?.topSalesPerson || 'No Sales Data',
      sub: monthlyData?.topSalesPerson
        ? `Sales Value: ${fmtOMR(monthlyData?.topSalesValue)}`
        : 'No sales data available',
      accent: PURPLE,
      bg: PURPLE_LIGHT,
      icon: '🏆',
    },
    {
      badge: monthlyData?.delayedProjects > 0 ? 'DELAYED PROJECTS' : 'PROJECT STATUS',
      title: monthlyData?.delayedProjects > 0
        ? `${monthlyData?.delayedProjects} Project${monthlyData?.delayedProjects !== 1 ? 's' : ''} Delayed`
        : 'All On Schedule',
      sub: monthlyData?.delayedProjects > 0
        ? 'Requires immediate management attention' :'No delays or risk indicators reported',
      accent: monthlyData?.delayedProjects > 0 ? RED : GREEN,
      bg: monthlyData?.delayedProjects > 0 ? RED_LIGHT : GREEN_LIGHT,
      icon: monthlyData?.delayedProjects > 0 ? '⚠' : '✅',
    },
  ];

  highlights?.forEach((h, i) => {
    const col = i % 3;
    const row = Math.floor(i / 3);
    drawHighlightCard(
      margin + col * (hlW + hlGap),
      y + row * (hlH + hlGap),
      hlW, hlH,
      h?.badge, h?.title, h?.sub, h?.accent, h?.bg, h?.icon
    );
  });
  y += 2 * (hlH + hlGap) + 8;

  // ── Business Health Score + Performance Indicators ──
  y = drawSectionTitle('Business Health Assessment', y, NAVY_MID, 'Composite performance score');

  const { score: healthScore, status: healthStatus, statusColor: healthStatusColor } = computeHealthScore();
  const healthW = 62;
  drawHealthGauge(margin, y, healthW, healthScore, healthStatus, healthStatusColor);

  // Performance indicators (right side)
  const pbX = margin + healthW + 8;
  const pbW = contentW - healthW - 8;

  pdf?.setFont('helvetica', 'bold');
  pdf?.setFontSize(8.5);
  setColor(...NAVY);
  pdf?.text('Performance Indicators', pbX, y + 8);

  const margin2 = monthlyData?.profitMargin || 0;
  const closedPct = monthlyData?.totalProjects > 0
    ? (monthlyData?.closedDeals / monthlyData?.totalProjects) * 100
    : 0;
  const revGrowthAbs = monthlyData?.prevRevenue > 0
    ? Math.min(100, Math.max(0, ((monthlyData?.totalRevenue || 0) / monthlyData?.prevRevenue) * 50))
    : 50;

  drawProgressBar(pbX, y + 14, pbW, Math.min(100, margin2 * 3), GREEN, 'Profit Margin', fmtPct(margin2));
  drawProgressBar(pbX, y + 24, pbW, Math.min(100, closedPct), ACCENT, 'Deal Closure Rate', `${closedPct?.toFixed(0)}%`);
  drawProgressBar(pbX, y + 34, pbW, revGrowthAbs, GOLD, 'Revenue vs Prior Month',
    monthlyData?.prevRevenue > 0
      ? `${revGrowth != null ? (revGrowth >= 0 ? '+' : '') + revGrowth?.toFixed(1) + '%' : 'N/A'}`
      : 'N/A'
  );

  // KPI summary row
  const summaryItems = [
    { label: 'Total Revenue', val: fmtOMR(monthlyData?.totalRevenue || 0), color: ACCENT },
    { label: 'Net Profit', val: fmtOMR(monthlyData?.totalProfit || 0), color: GREEN },
    { label: 'Closed Deals', val: String(monthlyData?.closedDeals ?? 0), color: [0, 138, 98] },
    { label: 'Pipeline Value', val: fmtOMR(monthlyData?.pipelineValue || 0), color: PURPLE },
  ];
  const siW = pbW / 4;
  summaryItems?.forEach((si, i) => {
    const sx = pbX + i * siW;
    setFill(...GRAY_LIGHT);
    setDraw(...GRAY_BORDER);
    pdf?.setLineWidth(0.2);
    pdf?.roundedRect(sx, y + 40, siW - 2, 16, 2, 2, 'FD');
    pdf?.setFont('helvetica', 'bold');
    pdf?.setFontSize(7.5);
    setColor(...si?.color);
    pdf?.text(si?.val, sx + (siW - 2) / 2, y + 50, { align: 'center' });
    pdf?.setFont('helvetica', 'normal');
    pdf?.setFontSize(5.8);
    setColor(...GRAY_MID);
    pdf?.text(si?.label, sx + (siW - 2) / 2, y + 54, { align: 'center' });
  });

  drawPageFooter(2);

  // ══════════════════════════════════════════════════════════════════
  // PAGE 3 — PERFORMANCE OVERVIEW (Charts)
  // ══════════════════════════════════════════════════════════════════
  pdf?.addPage();
  y = drawPageHeader(3, 'Performance Overview', `${monthName} ${selectedYear} — Revenue, Profit & Pipeline Analytics`);

  // ── Revenue & Profit Trend ──
  y = drawSectionTitle('Revenue & Profit Trend', y, ACCENT, `${selectedYear} Monthly Overview`);

  const trendData = yearlyData?.monthlyTrend || [];
  const chartData = trendData?.map(m => ({
    month: MONTHS_SHORT?.[m?.month - 1] || String(m?.month),
    monthNum: m?.month,
    revenue: m?.revenue || 0,
    profit: m?.profit || 0,
  }));

  // Legend
  setFill(...ACCENT);
  pdf?.roundedRect(pageW - margin - 44, y - 10, 6, 5, 1, 1, 'F');
  pdf?.setFont('helvetica', 'bold');
  pdf?.setFontSize(6.5);
  setColor(...GRAY_DARK);
  pdf?.text('Revenue', pageW - margin - 36, y - 6.5);
  setFill(...GREEN);
  pdf?.roundedRect(pageW - margin - 20, y - 10, 6, 5, 1, 1, 'F');
  pdf?.text('Profit', pageW - margin - 12, y - 6.5);

  if (chartData?.some(d => d?.revenue > 0 || d?.profit > 0)) {
    y = drawBarChart(margin, y, contentW, 62, chartData, 'month', 'revenue', ACCENT, 'profit', GREEN);
  } else {
    setFill(...GRAY_LIGHT);
    pdf?.roundedRect(margin, y, contentW, 22, 2, 2, 'F');
    pdf?.setFont('helvetica', 'italic');
    pdf?.setFontSize(8);
    setColor(...GRAY_MID);
    pdf?.text('No trend data available for this year', margin + contentW / 2, y + 14, { align: 'center' });
    y += 28;
  }

  // ── Project Status Breakdown ──
  y = drawSectionTitle('Project Status Breakdown', y, GREEN, `${monthName} ${selectedYear}`);

  const totalP = monthlyData?.totalProjects || 0;
  const closedP = monthlyData?.closedDeals || 0;
  const activeP2 = monthlyData?.activeProjects || 0;
  const delayedP = monthlyData?.delayedProjects || 0;
  const otherP = Math.max(0, totalP - closedP - activeP2 - delayedP);

  const pieSegments = [
    { label: 'Closed / Won', count: closedP, pct: totalP > 0 ? (closedP / totalP) * 100 : 0, color: GREEN },
    { label: 'Active', count: activeP2, pct: totalP > 0 ? (activeP2 / totalP) * 100 : 0, color: ACCENT },
    { label: 'Delayed', count: delayedP, pct: totalP > 0 ? (delayedP / totalP) * 100 : 0, color: RED },
    { label: 'Other', count: otherP, pct: totalP > 0 ? (otherP / totalP) * 100 : 0, color: GRAY_MID },
  ]?.filter(s => s?.count > 0);

  const pieR = 24;
  const pieCX = margin + pieR + 6;
  const pieCY = y + pieR + 4;

  if (totalP > 0 && pieSegments?.length > 0) {
    drawDonutChart(pieCX, pieCY, pieR, pieSegments);
    // Center label
    pdf?.setFont('helvetica', 'bold');
    pdf?.setFontSize(10);
    setColor(...NAVY);
    pdf?.text(String(totalP), pieCX, pieCY + 3.5, { align: 'center' });
    pdf?.setFont('helvetica', 'normal');
    pdf?.setFontSize(5.5);
    setColor(...GRAY_MID);
    pdf?.text('TOTAL', pieCX, pieCY + 9, { align: 'center' });

    // Legend
    const legendX = pieCX + pieR + 12;
    pieSegments?.forEach((seg, i) => {
      const ly = y + 4 + i * 13;
      setFill(...seg?.color);
      pdf?.roundedRect(legendX, ly, 9, 7, 1.5, 1.5, 'F');
      pdf?.setFont('helvetica', 'bold');
      pdf?.setFontSize(8.5);
      setColor(...GRAY_DARK);
      pdf?.text(String(seg?.count), legendX + 13, ly + 6);
      pdf?.setFont('helvetica', 'normal');
      pdf?.setFontSize(7);
      setColor(...GRAY_MID);
      pdf?.text(`${seg?.label}  (${seg?.pct?.toFixed(0)}%)`, legendX + 22, ly + 6);
    });

    // Stat cards (right half)
    const statX = margin + (contentW / 2) + 6;
    const statW2 = (contentW / 2 - 8) / 2;
    const statH2 = 24;
    const statItems = [
      { label: 'Total Projects', val: String(totalP), color: NAVY_MID, bg: GRAY_LIGHT },
      { label: 'Closed / Won', val: String(closedP), color: GREEN, bg: GREEN_LIGHT },
      { label: 'Active', val: String(activeP2), color: ACCENT, bg: ACCENT_LIGHT },
      { label: 'Delayed', val: String(delayedP), color: delayedP > 0 ? RED : GRAY_MID, bg: delayedP > 0 ? RED_LIGHT : GRAY_LIGHT },
    ];
    statItems?.forEach((s, i) => {
      const col = i % 2;
      const row = Math.floor(i / 2);
      const sx = statX + col * (statW2 + 4);
      const sy = y + row * (statH2 + 4);
      setFill(...s?.bg);
      setDraw(...GRAY_BORDER);
      pdf?.setLineWidth(0.2);
      pdf?.roundedRect(sx, sy, statW2, statH2, 2.5, 2.5, 'FD');
      setFill(...s?.color);
      pdf?.roundedRect(sx, sy, statW2, 2.5, 1.5, 1.5, 'F');
      pdf?.rect(sx, sy + 1.2, statW2, 1.3, 'F');
      pdf?.setFont('helvetica', 'bold');
      pdf?.setFontSize(14);
      setColor(...s?.color);
      pdf?.text(s?.val, sx + statW2 / 2, sy + 17, { align: 'center' });
      pdf?.setFont('helvetica', 'normal');
      pdf?.setFontSize(6.5);
      setColor(...GRAY_MID);
      pdf?.text(s?.label, sx + statW2 / 2, sy + 22, { align: 'center' });
    });
  } else {
    setFill(...GRAY_LIGHT);
    pdf?.roundedRect(margin, y, contentW, 18, 2, 2, 'F');
    pdf?.setFont('helvetica', 'italic');
    pdf?.setFontSize(8);
    setColor(...GRAY_MID);
    pdf?.text('No project data available for this period', margin + contentW / 2, y + 11, { align: 'center' });
  }
  y += pieR * 2 + 16;

  // ── Sales Pipeline Overview ──
  y = drawSectionTitle('Sales Pipeline Overview', y, PURPLE, 'Quotations & Deal Flow');

  const pipelineVal = monthlyData?.pipelineValue || 0;
  const openQCount = monthlyData?.openQuotations || 0;
  const closedVal = monthlyData?.totalRevenue || 0;
  const closedCount = monthlyData?.closedDeals || 0;

  const pipelineItems = [
    { label: 'Open Quotations', count: openQCount, value: pipelineVal, color: PURPLE, bg: PURPLE_LIGHT },
    { label: 'Closed / Won Deals', count: closedCount, value: closedVal, color: GREEN, bg: GREEN_LIGHT },
    { label: 'Total Pipeline', count: openQCount + closedCount, value: pipelineVal + closedVal, color: NAVY_MID, bg: ACCENT_LIGHT },
  ];

  const pipeW = (contentW - 10) / 3;
  pipelineItems?.forEach((item, i) => {
    const px = margin + i * (pipeW + 5);
    // Shadow
    setFill(215, 222, 238);
    pdf?.roundedRect(px + 0.7, y + 0.7, pipeW, 30, 2.5, 2.5, 'F');
    // Card
    setFill(...item?.bg);
    setDraw(...item?.color);
    pdf?.setLineWidth(0.35);
    pdf?.roundedRect(px, y, pipeW, 30, 2.5, 2.5, 'FD');
    pdf?.setLineWidth(0.2);
    // Top bar
    setFill(...item?.color);
    pdf?.roundedRect(px, y, pipeW, 3, 1.5, 1.5, 'F');
    pdf?.rect(px, y + 1.5, pipeW, 1.5, 'F');
    // Label
    pdf?.setFont('helvetica', 'normal');
    pdf?.setFontSize(6.5);
    setColor(...GRAY_MID);
    pdf?.text(item?.label?.toUpperCase(), px + pipeW / 2, y + 10, { align: 'center' });
    // Count
    pdf?.setFont('helvetica', 'bold');
    pdf?.setFontSize(16);
    setColor(...item?.color);
    pdf?.text(String(item?.count), px + pipeW / 2, y + 21, { align: 'center' });
    // Value
    pdf?.setFont('helvetica', 'normal');
    pdf?.setFontSize(7);
    setColor(...GRAY_MID);
    pdf?.text(fmtOMR(item?.value), px + pipeW / 2, y + 28, { align: 'center' });
  });

  drawPageFooter(3);

  // ══════════════════════════════════════════════════════════════════
  // PAGE 4 — MANAGEMENT INSIGHTS & RECOMMENDATIONS
  // ══════════════════════════════════════════════════════════════════
  pdf?.addPage();
  y = drawPageHeader(4, 'Management Insights & Recommendations', `${monthName} ${selectedYear} — Strategic Actions & CEO Commentary`);

  // ── Section 1: What Is Going Well ──
  y = drawSectionTitle('What Is Going Well', y, GREEN, 'Positive performance indicators');

  const goingWell = [];
  if ((monthlyData?.totalRevenue || 0) > 0) goingWell?.push('Revenue Generation — Active revenue recorded for the reporting period.');
  if ((monthlyData?.prevRevenue || 0) > 0 && (monthlyData?.totalRevenue || 0) >= (monthlyData?.prevRevenue || 0)) goingWell?.push('Revenue Growth — Revenue maintained or improved vs previous month.');
  if ((monthlyData?.profitMargin || 0) >= 15) goingWell?.push(`Strong Profit Margin — ${fmtPct(monthlyData?.profitMargin)} margin reflects healthy profitability.`);
  if ((monthlyData?.closedDeals || 0) > 0) goingWell?.push(`Closed Deals — ${monthlyData?.closedDeals} deal${monthlyData?.closedDeals !== 1 ? 's' : ''} successfully closed this period.`);
  if ((monthlyData?.activeProjects || 0) > 0) goingWell?.push(`Active Project Pipeline — ${monthlyData?.activeProjects} project${monthlyData?.activeProjects !== 1 ? 's' : ''} currently in execution.`);
  if ((monthlyData?.openQuotations || 0) > 0) goingWell?.push(`Healthy Sales Pipeline — ${monthlyData?.openQuotations} open quotation${monthlyData?.openQuotations !== 1 ? 's' : ''} in the pipeline.`);
  if (goingWell?.length === 0) goingWell?.push('Operations are ongoing. Continue monitoring KPIs for improvement opportunities.');

  const gwColW = (contentW - 5) / 2;
  goingWell?.slice(0, 4)?.forEach((item, i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const gx = margin + col * (gwColW + 5);
    const gy = y + row * 13;
    setFill(...GREEN_LIGHT);
    setDraw(...GREEN);
    pdf?.setLineWidth(0.25);
    pdf?.roundedRect(gx, gy, gwColW, 11, 2, 2, 'FD');
    pdf?.setLineWidth(0.2);
    setFill(...GREEN);
    pdf?.roundedRect(gx, gy, 3.5, 11, 1.5, 1.5, 'F');
    pdf?.rect(gx + 2, gy, 1.5, 11, 'F');
    pdf?.setFont('helvetica', 'bold');
    pdf?.setFontSize(7.5);
    setColor(...GREEN);
    pdf?.text('✓', gx + 6, gy + 7.5);
    pdf?.setFont('helvetica', 'normal');
    pdf?.setFontSize(7);
    setColor(...GRAY_DARK);
    const itemLines = pdf?.splitTextToSize(item, gwColW - 20);
    pdf?.text(itemLines?.[0], gx + 14, gy + 7.5);
  });
  y += Math.ceil(Math.min(goingWell?.length, 4) / 2) * 13 + 5;

  // ── Section 2: Areas Requiring Attention ──
  y = drawSectionTitle('Areas Requiring Attention', y, AMBER, 'Items needing management focus');

  const attention = [];
  if ((monthlyData?.outstandingPayments || 0) > 0) attention?.push(`Pending Collections — ${fmtOMR(monthlyData?.outstandingPayments)} in outstanding receivables requires follow-up.`);
  if ((monthlyData?.delayedProjects || 0) > 0) attention?.push(`Delayed Projects — ${monthlyData?.delayedProjects} project${monthlyData?.delayedProjects !== 1 ? 's' : ''} behind schedule. Immediate escalation required.`);
  if ((monthlyData?.openQuotations || 0) > 3) attention?.push(`Low Conversion — ${monthlyData?.openQuotations} open quotations need active follow-up to convert to closed deals.`);
  if ((monthlyData?.profitMargin || 0) < 10 && (monthlyData?.profitMargin || 0) > 0) attention?.push(`Margin Pressure — Profit margin at ${fmtPct(monthlyData?.profitMargin)} is below the 10% threshold. Review pricing.`);
  if (attention?.length === 0) attention?.push('No critical issues identified. Continue monitoring all KPIs for early warning signs.');

  attention?.slice(0, 3)?.forEach((item, i) => {
    const gy = y + i * 13;
    setFill(...AMBER_LIGHT);
    setDraw(...AMBER);
    pdf?.setLineWidth(0.25);
    pdf?.roundedRect(margin, gy, contentW, 11, 2, 2, 'FD');
    pdf?.setLineWidth(0.2);
    setFill(...AMBER);
    pdf?.roundedRect(margin, gy, 3.5, 11, 1.5, 1.5, 'F');
    pdf?.rect(margin + 2, gy, 1.5, 11, 'F');
    pdf?.setFont('helvetica', 'bold');
    pdf?.setFontSize(7.5);
    setColor(...AMBER);
    pdf?.text('!', margin + 6, gy + 7.5);
    pdf?.setFont('helvetica', 'normal');
    pdf?.setFontSize(7);
    setColor(...GRAY_DARK);
    const itemLines = pdf?.splitTextToSize(item, contentW - 20);
    pdf?.text(itemLines?.[0], margin + 14, gy + 7.5);
  });
  y += Math.min(attention?.length, 3) * 13 + 5;

  // ── Section 3: Executive Recommendations ──
  y = drawSectionTitle('Executive Recommendations', y, NAVY_MID, 'Data-driven action items');

  const recommendations = generateRecommendations();
  recommendations?.forEach((rec, i) => {
    const recLines = pdf?.splitTextToSize(rec, contentW - 30);
    const recH = Math.max(13, recLines?.length * 4.8 + 9);
    // Shadow
    setFill(215, 222, 238);
    pdf?.roundedRect(margin + 0.6, y + 0.6, contentW, recH, 2, 2, 'F');
    // Card
    setFill(...OFF_WHITE);
    setDraw(...GRAY_BORDER);
    pdf?.setLineWidth(0.2);
    pdf?.roundedRect(margin, y, contentW, recH, 2, 2, 'FD');
    // Number badge
    setFill(...NAVY_MID);
    pdf?.circle(margin + 7, y + recH / 2, 5, 'F');
    pdf?.setFont('helvetica', 'bold');
    pdf?.setFontSize(7);
    setColor(...WHITE);
    pdf?.text(String(i + 1), margin + 7, y + recH / 2 + 2.5, { align: 'center' });
    // Check icon
    pdf?.setFont('helvetica', 'bold');
    pdf?.setFontSize(8.5);
    setColor(...GREEN);
    pdf?.text('✓', margin + 16, y + recH / 2 + 3);
    // Text
    pdf?.setFont('helvetica', 'normal');
    pdf?.setFontSize(7.8);
    setColor(...GRAY_DARK);
    pdf?.text(recLines, margin + 25, y + 8);
    y += recH + 3;
  });

  y += 4;

  // ── CEO Corner ──
  if (y < pageH - 52) {
    // CEO Corner header
    setFill(...NAVY);
    pdf?.roundedRect(margin, y, contentW, 10, 2.5, 2.5, 'F');
    setFill(...GOLD_BRIGHT);
    pdf?.roundedRect(margin, y, contentW, 2.5, 1.5, 1.5, 'F');
    pdf?.rect(margin, y + 1.2, contentW, 1.3, 'F');
    pdf?.setFont('helvetica', 'bold');
    pdf?.setFontSize(8.5);
    setColor(...GOLD_BRIGHT);
    pdf?.text('CEO CORNER  —  Executive Commentary', pageW / 2, y + 7, { align: 'center' });
    y += 12;

    const ceoText = generateCEOCorner();
    const ceoLines = pdf?.splitTextToSize(`"${ceoText}"`, contentW - 18);
    const ceoH = Math.max(24, ceoLines?.length * 4.8 + 16);

    // CEO block background
    setFill(247, 249, 255);
    setDraw(...NAVY_MID);
    pdf?.setLineWidth(0.35);
    pdf?.roundedRect(margin, y, contentW, ceoH, 2.5, 2.5, 'FD');
    pdf?.setLineWidth(0.2);
    // Left accent
    setFill(...NAVY_MID);
    pdf?.roundedRect(margin, y, 4.5, ceoH, 1.5, 1.5, 'F');
    pdf?.rect(margin + 2.5, y, 2, ceoH, 'F');

    // Large quote mark
    pdf?.setFont('helvetica', 'bold');
    pdf?.setFontSize(32);
    setColor(218, 226, 248);
    pdf?.text('"', margin + 8, y + 16);

    // CEO text
    pdf?.setFont('helvetica', 'italic');
    pdf?.setFontSize(7.8);
    setColor(...GRAY_DARK);
    pdf?.text(ceoLines, margin + 8, y + 12);

    // Signature
    pdf?.setFont('helvetica', 'bold');
    pdf?.setFontSize(7);
    setColor(...NAVY_MID);
    pdf?.text('— Executive Management', margin + contentW - 4, y + ceoH - 4, { align: 'right' });

    // Gold bottom accent
    setFill(...GOLD_BRIGHT);
    pdf?.roundedRect(margin, y + ceoH - 2, contentW, 2, 1, 1, 'F');
  }

  drawPageFooter(4);

  pdf?.save(`Executive_Brief_${MONTHS?.[selectedMonth - 1]}_${selectedYear}.pdf`);
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ExecutiveSnapshot = () => {
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedDept, setSelectedDept] = useState('');
  const [departments, setDepartments] = useState([]);

  const [monthlyData, setMonthlyData] = useState(null);
  const [yearlyData, setYearlyData] = useState(null);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [yearlyLoading, setYearlyLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // ── Validation state ──
  const [monthlyValidation, setMonthlyValidation] = useState(null);
  const [yearlyValidation, setYearlyValidation] = useState(null);

  useEffect(() => {
    fetchDepartments()?.then(setDepartments)?.catch(() => {});
  }, []);

  const loadMonthly = useCallback(async () => {
    setMonthlyLoading(true);
    try {
      const data = await fetchMonthlySnapshot({
        month: selectedMonth,
        year: selectedYear,
        department: selectedDept || null,
      });
      setMonthlyData(data);
      setMonthlyValidation(validateMonthlyKPIs(data));
    } finally {
      setMonthlyLoading(false);
    }
  }, [selectedMonth, selectedYear, selectedDept]);

  const loadYearly = useCallback(async () => {
    setYearlyLoading(true);
    try {
      const data = await fetchYearlySnapshot({
        year: selectedYear,
        department: selectedDept || null,
      });
      setYearlyData(data);
      setYearlyValidation(validateYearlyKPIs(data));
    } finally {
      setYearlyLoading(false);
    }
  }, [selectedYear, selectedDept]);

  useEffect(() => { loadMonthly(); }, [loadMonthly]);
  useEffect(() => { loadYearly(); }, [loadYearly]);

  const handleExportPDF = async () => {
    setExportLoading(true);
    try {
      const pdfValidation = validatePDFData(monthlyData, yearlyData);
      await buildPDF({ monthlyData, yearlyData, selectedMonth, selectedYear, selectedDept, pdfValidation });
    } catch (err) {
      console.error('PDF export error:', err);
    } finally {
      setExportLoading(false);
    }
  };

  const revTrend = calcTrend(monthlyData?.totalRevenue, monthlyData?.prevRevenue);

  const getMonthlyFieldIssue = (fieldName) =>
    monthlyValidation?.issues?.find(i => i?.field === fieldName) || null;
  const getYearlyFieldIssue = (fieldName) =>
    yearlyValidation?.issues?.find(i => i?.field === fieldName) || null;

  const monthlyKPIs = [
    {
      title: 'Revenue',
      value: fmtOMR(monthlyData?.totalRevenue || monthlyData?.projectRevenue),
      icon: 'DollarSign',
      color: 'blue',
      trend: revTrend?.trend,
      trendValue: revTrend?.trendValue,
      subtitle: monthlyData?.totalRevenue > 0 ? 'Closed deals value' : monthlyData?.projectRevenue > 0 ? 'Active project value' : 'No closed deals yet',
      validationIssue: getMonthlyFieldIssue('totalRevenue'),
    },
    {
      title: 'Profit',
      value: fmtOMR(monthlyData?.totalProfit || monthlyData?.projectProfit),
      icon: 'TrendingUp',
      color: 'emerald',
      subtitle: monthlyData?.totalProfit > 0 ? 'Net profit this month' : monthlyData?.projectProfit > 0 ? 'Active project profit' : 'No profit recorded',
      validationIssue: getMonthlyFieldIssue('totalProfit'),
    },
    {
      title: 'Profit Margin',
      value: fmtPct(monthlyData?.profitMargin),
      icon: 'Percent',
      color: 'violet',
      subtitle: 'Profit / Revenue ratio',
      validationIssue: getMonthlyFieldIssue('profitMargin'),
    },
    {
      title: 'Closed Deals',
      value: monthlyData == null ? '—' : String(monthlyData?.closedDeals ?? 0),
      icon: 'Handshake',
      color: 'teal',
      subtitle: 'Won & finalized',
      validationIssue: getMonthlyFieldIssue('closedDeals'),
    },
    {
      title: 'Open Quotations',
      value: monthlyData == null ? '—' : String(monthlyData?.openQuotations ?? 0),
      icon: 'FileText',
      color: 'amber',
      subtitle: 'Pending proposals',
      validationIssue: getMonthlyFieldIssue('openQuotations'),
    },
    {
      title: 'Total Projects',
      value: monthlyData == null ? '—' : String(monthlyData?.totalProjects ?? 0),
      icon: 'FolderOpen',
      color: 'sky',
      subtitle: 'All projects this month',
      validationIssue: getMonthlyFieldIssue('totalProjects') || getMonthlyFieldIssue('projectCounts'),
    },
    {
      title: 'In Progress',
      value: monthlyData == null ? '—' : String(monthlyData?.inProgressProjects ?? 0),
      icon: 'PlayCircle',
      color: 'indigo',
      subtitle: 'Currently executing',
    },
    {
      title: 'Pipeline Value',
      value: fmtOMR(monthlyData?.pipelineValue),
      icon: 'TrendingUp',
      color: 'orange',
      subtitle: 'Open & active projects',
      validationIssue: getMonthlyFieldIssue('pipelineValue'),
    },
  ];

  const yearlyKPIs = [
    {
      title: 'Revenue YTD',
      value: fmtOMR(yearlyData?.totalRevenue || yearlyData?.projectRevenue),
      icon: 'DollarSign',
      color: 'blue',
      subtitle: yearlyData?.totalRevenue > 0 ? 'Year-to-date revenue' : yearlyData?.projectRevenue > 0 ? 'Active project value' : 'Year-to-date revenue',
      validationIssue: getYearlyFieldIssue('totalRevenue'),
    },
    {
      title: 'Profit YTD',
      value: fmtOMR(yearlyData?.totalProfit || yearlyData?.projectProfit),
      icon: 'TrendingUp',
      color: 'emerald',
      subtitle: 'Year-to-date profit',
      validationIssue: getYearlyFieldIssue('totalProfit'),
    },
    {
      title: 'Profit Margin',
      value: fmtPct(yearlyData?.profitMargin),
      icon: 'Percent',
      color: 'violet',
      subtitle: 'Annual margin %',
    },
    {
      title: 'Total Projects',
      value: yearlyData == null ? '—' : String(yearlyData?.totalProjects ?? 0),
      icon: 'FolderOpen',
      color: 'sky',
      subtitle: 'All projects this year',
    },
    {
      title: 'Active Projects',
      value: yearlyData == null ? '—' : String(yearlyData?.activeProjects ?? 0),
      icon: 'PlayCircle',
      color: 'indigo',
      subtitle: 'In execution',
    },
    {
      title: 'Completed',
      value: yearlyData == null ? '—' : String(yearlyData?.completedProjects ?? 0),
      icon: 'CheckCircle',
      color: 'green',
      subtitle: 'Finished this year',
    },
    {
      title: 'Best Month',
      value: yearlyData?.bestMonth || (yearlyData == null ? '—' : 'No Data'),
      icon: 'Star',
      color: 'orange',
      subtitle: yearlyData?.bestMonthRevenue ? fmtOMR(yearlyData?.bestMonthRevenue) : 'Highest revenue month',
      validationIssue: getYearlyFieldIssue('bestMonth'),
    },
    {
      title: 'Top Department',
      value: yearlyData?.topDepartment || (yearlyData == null ? '—' : 'No Data'),
      icon: 'Building2',
      color: 'rose',
      subtitle: yearlyData?.topDepartmentRevenue ? fmtOMR(yearlyData?.topDepartmentRevenue) : 'By revenue',
    },
  ];

  const isLoading = monthlyLoading || yearlyLoading;

  return (
    <MainLayout>
      <div className="min-h-screen bg-background">
        {/* ── Sticky Header ── */}
        <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-sm border-b border-border">
          <div className="px-4 md:px-6 py-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 max-w-[1400px] mx-auto">
              {/* Title */}
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Icon name="LayoutDashboard" size={18} className="text-white" />
                </div>
                <div>
                  <h1 className="text-base font-bold text-foreground leading-tight">Executive Snapshot</h1>
                  <p className="text-xs text-muted-foreground">
                    {MONTHS?.[selectedMonth - 1]} {selectedYear}
                    {selectedDept ? ` · ${selectedDept}` : ''}
                    {isLoading && (
                      <span className="ml-2 inline-flex items-center gap-1 text-blue-500">
                        <Icon name="Loader2" size={10} className="animate-spin" />
                        Loading...
                      </span>
                    )}
                  </p>
                </div>
              </div>

              {/* Controls */}
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={selectedMonth}
                  onChange={e => setSelectedMonth(Number(e?.target?.value))}
                  className="h-8 px-2.5 text-xs bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                >
                  {MONTHS?.map((m, i) => (
                    <option key={i} value={i + 1}>{m}</option>
                  ))}
                </select>

                <select
                  value={selectedYear}
                  onChange={e => setSelectedYear(Number(e?.target?.value))}
                  className="h-8 px-2.5 text-xs bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                >
                  {YEARS?.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>

                {departments?.length > 0 && (
                  <select
                    value={selectedDept}
                    onChange={e => setSelectedDept(e?.target?.value)}
                    className="h-8 px-2.5 text-xs bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                  >
                    <option value="">All Departments</option>
                    {departments?.map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                )}

                <button
                  onClick={handleExportPDF}
                  disabled={exportLoading || isLoading}
                  className="flex items-center gap-1.5 h-8 px-3.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-semibold rounded-lg transition-colors"
                >
                  {exportLoading ? (
                    <><Icon name="Loader2" size={12} className="animate-spin" />Exporting...</>
                  ) : (
                    <><Icon name="Download" size={12} />Export PDF</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Page Content ── */}
        <div className="px-4 md:px-6 py-6 space-y-7 max-w-[1400px] mx-auto">

          {/* ══ SECTION 1: Monthly KPIs ══ */}
          <section>
            <SectionHeader
              icon="BarChart2"
              iconColor="bg-blue-100 dark:bg-blue-900/40 text-blue-600"
              title="Company Monthly Snapshot"
              badge={`${MONTHS?.[selectedMonth - 1]} ${selectedYear}`}
              subtitle="Key performance indicators for the selected month"
            />

            {!monthlyLoading && (
              <div className="mb-4">
                <ValidationBanner result={monthlyValidation} section="Monthly Data" compact />
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
              {monthlyKPIs?.map((kpi, i) => (
                <SnapshotKPICard key={i} {...kpi} loading={monthlyLoading} />
              ))}
            </div>

            {!monthlyLoading && monthlyData && (
              <div className="mt-4">
                <StatSummaryBar items={[
                  {
                    label: 'Total Projects',
                    value: String(monthlyData?.totalProjects ?? 0),
                    color: 'text-sky-600',
                  },
                  {
                    label: 'In Progress',
                    value: String(monthlyData?.inProgressProjects ?? 0),
                    color: 'text-indigo-600',
                  },
                  {
                    label: 'Completed',
                    value: String(monthlyData?.completedProjects ?? 0),
                    color: 'text-emerald-600',
                  },
                  {
                    label: 'Delayed / At Risk',
                    value: monthlyData?.delayedProjects > 0 ? `${monthlyData?.delayedProjects} at risk` : 'All on track',
                    color: monthlyData?.delayedProjects > 0 ? 'text-red-500' : 'text-emerald-600',
                  },
                ]} />
              </div>
            )}
          </section>

          {/* ══ SECTION 2: Highlights + AI Insights ══ */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="bg-card border border-border rounded-2xl p-5">
              <SectionHeader
                icon="Pin"
                iconColor="bg-amber-100 dark:bg-amber-900/40 text-amber-600"
                title="Monthly Highlights"
                subtitle="Key business achievements and alerts"
              />
              {!monthlyLoading && monthlyValidation && (
                <div className="mb-3">
                  <ValidationBanner
                    result={{
                      severity: monthlyValidation?.severity,
                      issues: monthlyValidation?.issues?.filter(i =>
                        ['largestDeal', 'topCustomer', 'topDepartment', 'topSalesPerson']?.includes(i?.field)
                      ),
                    }}
                    section="Highlights"
                    compact
                  />
                </div>
              )}
              <MonthlyHighlights data={monthlyData} loading={monthlyLoading} />
            </div>

            <AIInsightPanel data={monthlyData} type="monthly" loading={monthlyLoading} />
          </div>

          {/* ══ SECTION 3: Yearly KPIs ══ */}
          <section>
            <SectionHeader
              icon="TrendingUp"
              iconColor="bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600"
              title="Yearly Performance Snapshot"
              badge={String(selectedYear)}
              subtitle="Year-to-date cumulative performance metrics"
            />

            {!yearlyLoading && (
              <div className="mb-4">
                <ValidationBanner result={yearlyValidation} section="Yearly Data" compact />
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
              {yearlyKPIs?.map((kpi, i) => (
                <SnapshotKPICard key={i} {...kpi} loading={yearlyLoading} />
              ))}
            </div>
          </section>

          {/* ══ SECTION 4: Trend Chart ══ */}
          <div className="bg-card border border-border rounded-2xl p-5" id="exec-trend-chart">
            <SectionHeader
              icon="LineChart"
              iconColor="bg-sky-100 dark:bg-sky-900/40 text-sky-600"
              title="Monthly Revenue & Profit Trend"
              badge={String(selectedYear)}
              subtitle="Revenue bars vs Profit line — hover for exact values"
            />
            {!yearlyLoading && yearlyValidation && (
              <div className="mb-3">
                <ValidationBanner
                  result={{
                    severity: yearlyValidation?.severity,
                    issues: yearlyValidation?.issues?.filter(i => i?.field === 'monthlyTrend'),
                  }}
                  section="Trend Chart"
                  compact
                />
              </div>
            )}
            <MonthlyTrendChart data={yearlyData?.monthlyTrend || []} loading={yearlyLoading} />
          </div>

          {/* ══ SECTION 5: Yearly Highlights ══ */}
          <div className="bg-card border border-border rounded-2xl p-5">
            <SectionHeader
              icon="Trophy"
              iconColor="bg-amber-100 dark:bg-amber-900/40 text-amber-600"
              title="Yearly Business Highlights"
              subtitle="Top performers, best deals, and department rankings"
            />
            {!yearlyLoading && yearlyValidation && (
              <div className="mb-3">
                <ValidationBanner
                  result={{
                    severity: yearlyValidation?.severity,
                    issues: yearlyValidation?.issues?.filter(i =>
                      ['top5Customers', 'top5Deals', 'bestMonth', 'avgMonthlyRevenue']?.includes(i?.field)
                    ),
                  }}
                  section="Yearly Highlights"
                  compact
                />
              </div>
            )}
            <YearlyHighlights data={yearlyData} loading={yearlyLoading} />
          </div>

          {/* ══ SECTION 6: Executive Annual Comment ══ */}
          <AIInsightPanel data={yearlyData} type="yearly" loading={yearlyLoading} />

        </div>
      </div>
    </MainLayout>
  );
};

export default ExecutiveSnapshot;
